import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { Col, Input, Row } from "reactstrap"
import DatePicker from "react-datepicker"
import moment from "moment"

const FREQ_OPTIONS = [
  { value: "S", labelKey: "contaspagar.recorrencia.frequencia.semanal" },
  { value: "Q", labelKey: "contaspagar.recorrencia.frequencia.quinzenal" },
  { value: "M", labelKey: "contaspagar.recorrencia.frequencia.mensal" },
  { value: "B", labelKey: "contaspagar.recorrencia.frequencia.bimestral" },
  { value: "T", labelKey: "contaspagar.recorrencia.frequencia.trimestral" },
  { value: "E", labelKey: "contaspagar.recorrencia.frequencia.semestral" },
  { value: "A", labelKey: "contaspagar.recorrencia.frequencia.anual" },
]

const REGRA_OPTIONS = [
  { value: "M", labelKey: "contaspagar.recorrencia.regra_vencimento.manter" },
  { value: "A", labelKey: "contaspagar.recorrencia.regra_vencimento.antecipar" },
  { value: "P", labelKey: "contaspagar.recorrencia.regra_vencimento.postergar" },
]

const momentWeekdayToBackend = m => m.day() + 1

// A data inicial (vencimento inicial) é exibida pelo modal, na mesma linha do
// tipo de cobrança. Aqui ficam apenas frequência, término e regra de fim de semana.
const RecorrenciaFields = props => {
  const { t, value, onChange } = props
  const v = value || {}

  const showDiaSemana = v.frequencia === "S" || v.frequencia === "Q"

  const diaSemanaOptions = useMemo(() => (
    [1, 2, 3, 4, 5, 6, 7].map(d => ({
      value: d,
      label: t(`contaspagar.recorrencia.dia_semana.${ d }`),
    }))
  ), [t])

  useEffect(() => {
    if (showDiaSemana && v.data_inicio && !v.dia_semana) {
      onChange({ ...v, dia_semana: momentWeekdayToBackend(moment(v.data_inicio)) })
    }
    if (!showDiaSemana && v.dia_semana != null) {
      onChange({ ...v, dia_semana: null })
    }
  }, [showDiaSemana, v.data_inicio])

  const setField = (field, value) => onChange({ ...v, [field]: value })

  const onChangeDiaSemana = e => {
    const novoDia = parseInt(e.target.value, 10)
    const update = { ...v, dia_semana: novoDia }
    if (v.data_inicio) {
      const m = moment(v.data_inicio)
      const diff = ((novoDia - 1) - m.day() + 7) % 7
      update.data_inicio = m.add(diff || 7, "days").toDate()
    }
    onChange(update)
  }

  const onChangeTermina = tipo => onChange({
    ...v,
    termina_em: tipo,
    data_fim: tipo === "data" ? v.data_fim : null,
    qtd_recorrencias: tipo === "qtd" ? (v.qtd_recorrencias || 12) : null,
  })

  const terminaEm = v.termina_em || "qtd"

  return (
    <div className="cp-form-grid">
      <Row>
        <Col md="6">
          <div className="mb-4">
            <label className="cp-field-label">
              {t("contaspagar.recorrencia.frequencia")}
            </label>
            <select
              className="form-select"
              value={v.frequencia || "M"}
              onChange={e => setField("frequencia", e.target.value)}
            >
              {FREQ_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </Col>

        <Col md="6">
          <div className="mb-4">
            <label className="cp-field-label">
              {t("contaspagar.recorrencia.termina_em")}
            </label>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-check-input mt-0"
                  type="radio"
                  id="termina_em_qtd"
                  checked={terminaEm === "qtd"}
                  onChange={() => onChangeTermina("qtd")}
                />
                <label
                  className="form-check-label text-nowrap"
                  htmlFor="termina_em_qtd"
                >
                  {t("contaspagar.recorrencia.termina_em.quantidade")}
                </label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={v.qtd_recorrencias || ""}
                  onChange={e =>
                    setField(
                      "qtd_recorrencias",
                      parseInt(e.target.value, 10) || null
                    )
                  }
                  disabled={terminaEm !== "qtd"}
                  style={{ width: 90 }}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-check-input mt-0"
                  type="radio"
                  id="termina_em_data"
                  checked={terminaEm === "data"}
                  onChange={() => onChangeTermina("data")}
                />
                <label
                  className="form-check-label text-nowrap"
                  htmlFor="termina_em_data"
                >
                  {t("contaspagar.recorrencia.termina_em.data")}
                </label>
                <DatePicker
                  selected={v.data_fim}
                  onChange={date => setField("data_fim", date)}
                  className="form-control"
                  placeholderText={t("contaspagar.filtro.placeholder.data")}
                  locale="ptBR"
                  dateFormat="dd/MM/yyyy"
                  disabled={terminaEm !== "data"}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {showDiaSemana && (
        <Row>
          <Col md="6">
            <div className="mb-4">
              <label className="cp-field-label">
                {t("contaspagar.recorrencia.dia_semana")}
              </label>
              <select
                className="form-select"
                value={v.dia_semana || ""}
                onChange={onChangeDiaSemana}
              >
                {diaSemanaOptions.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col md="12">
          <div className="mb-4">
            <label className="cp-field-label">
              {t("contaspagar.recorrencia.regra_vencimento")}
              <i
                className="mdi mdi-help-circle-outline ms-1"
                title={t("contaspagar.recorrencia.regra_vencimento.tooltip")}
              />
            </label>
            <div className="d-flex flex-wrap gap-4">
              {REGRA_OPTIONS.map(o => (
                <div className="form-check" key={o.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`regra_vencimento_${o.value}`}
                    checked={(v.regra_vencimento || "M") === o.value}
                    onChange={() => setField("regra_vencimento", o.value)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`regra_vencimento_${o.value}`}
                  >
                    {t(o.labelKey)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

RecorrenciaFields.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

export default withTranslation()(RecorrenciaFields)
